import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SEO from '../../components/SEO';
import ShareBox from '../../components/ShareBox';
import { 
  TrendingUp, TrendingDown, ArrowRight, ArrowLeft,
  ChevronRight, Zap, AlertTriangle, CheckCircle, Clock,
  BarChart3, Activity, Target, Brain, Percent
} from 'lucide-react';

// FAQ Data
const faqData = [
  {
    question: "Fibonacci seviyeleri nelerdir?",
    answer: "En yaygın Fibonacci geri çekilme seviyeleri %23.6, %38.2, %50, %61.8 ve %78.6'dır. Uzatma seviyeleri ise %127.2, %161.8, %200 ve %261.8'dir. %61.8 'altın oran' olarak bilinir ve en önemli seviye kabul edilir."
  },
  {
    question: "Fibonacci retracement nasıl çizilir?",
    answer: "Yükseliş trendinde dip noktadan zirveye, düşüş trendinde zirveden dip noktaya doğru çizilir. Platform otomatik olarak seviyeleri hesaplar. Önemli olan doğru swing noktalarını seçmektir."
  },
  {
    question: "Fibonacci %61.8 neden önemli?",
    answer: "%61.8, matematiksel olarak 'altın oran'a karşılık gelir ve doğada sıkça görülür. Trading'de bu seviye genellikle en güçlü geri çekilme desteği veya direnci olarak kabul edilir."
  },
  {
    question: "Fibonacci ile nasıl trade yapılır?",
    answer: "1) Geri çekilme seviyelerinde (özellikle %38.2-%61.8) destek/direnç arayın, 2) Bu seviyelerde RSI veya MACD konfirmasyonu bekleyin, 3) Uzatma seviyeleri kar alma hedefi olarak kullanılabilir."
  },
  {
    question: "Fibonacci her zaman çalışır mı?",
    answer: "Hayır. Fibonacci seviyeleri kesin destek/direnç değildir, olasılık bölgeleridir. Diğer göstergeler ve price action ile birlikte değerlendirilmelidir. Her zaman stop-loss kullanın."
  }
];

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Fibonacci Nedir? Fibonacci Retracement ve Extension Kullanımı",
    "description": "Fibonacci seviyeleri nedir, nasıl çizilir ve nasıl yorumlanır? Kripto trading'de Fibonacci retracement ve extension kullanımı rehberi.",
    "author": { "@type": "Organization", "name": "Borsamex" },
    "publisher": { "@type": "Organization", "name": "Borsamex", "logo": { "@type": "ImageObject", "url": "https://borsamex.com/logo512.png" } },
    "datePublished": "2026-04-02",
    "dateModified": "2026-04-02",
    "mainEntityOfPage": "https://borsamex.com/rehber/fibonacci-nedir"
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({ "@type": "Question", "name": item.question, "acceptedAnswer": { "@type": "Answer", "text": item.answer } }))
  }
];

// Fibonacci seviyeleri
const fibLevels = [
  { level: "0%", desc: "Başlangıç noktası", color: "text-slate-400" },
  { level: "23.6%", desc: "Zayıf geri çekilme", color: "text-blue-400" },
  { level: "38.2%", desc: "Orta geri çekilme", color: "text-green-400" },
  { level: "50%", desc: "Yarı seviye", color: "text-yellow-400" },
  { level: "61.8%", desc: "Altın oran - En önemli", color: "text-brand-green" },
  { level: "78.6%", desc: "Derin geri çekilme", color: "text-orange-400" },
  { level: "100%", desc: "Tam geri çekilme", color: "text-red-400" },
];

const FibonacciNedirPage = () => {
  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO 
        title="Fibonacci Nedir? Fibonacci Retracement ve Extension Kullanımı | Borsamex"
        description="Fibonacci seviyeleri nedir, nasıl çizilir ve nasıl yorumlanır? Kripto trading'de Fibonacci retracement, %61.8 altın oran ve extension seviyeleri rehberi."
        keywords="fibonacci nedir, fibonacci retracement, fibonacci seviyeleri, fibonacci nasıl çizilir, fibonacci 61.8, altın oran, fibonacci extension, kripto teknik analiz"
        canonical="https://borsamex.com/rehber/fibonacci-nedir"
        structuredData={structuredData}
      />

      {/* Breadcrumb */}
      <div className="border-b border-slate-800/50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-white">Ana Sayfa</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/rehber" className="hover:text-white">Rehber</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">Fibonacci Nedir?</span>
          </div>
        </div>
      </div>

      <article className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          
          {/* Header */}
          <motion.header initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">Teknik Analiz</Badge>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Clock className="h-4 w-4" />
                <span>8 dk okuma</span>
              </div>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
              Fibonacci Nedir? (Fibonacci Retracement)
            </h1>
            
            <div className="p-6 bg-slate-900/50 border border-slate-700/50 rounded-xl">
              <p className="text-lg text-slate-300 leading-relaxed">
                <strong className="text-white">Fibonacci seviyeleri</strong>, fiyatın potansiyel geri çekilme 
                ve devam noktalarını belirlemek için kullanılan teknik analiz aracıdır. En önemli seviye olan 
                <strong className="text-brand-green"> %61.8 "altın oran"</strong> olarak bilinir ve 
                doğada matematiksel olarak sıkça karşılaşılır.
              </p>
            </div>
          </motion.header>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="prose prose-invert prose-slate max-w-none">
            
            {/* Section 1 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Percent className="h-4 w-4 text-amber-400" />
                </div>
                Fibonacci Seviyeleri Nelerdir?
              </h2>
              
              <p className="text-slate-300 mb-6">
                13. yüzyılda İtalyan matematikçi Leonardo Fibonacci tarafından keşfedilen sayı dizisi, 
                doğada ve finansal piyasalarda sıkça görülen oranlar üretir. Trading'de bu oranlar 
                potansiyel destek ve direnç seviyeleri olarak kullanılır.
              </p>
              
              <Card className="border-slate-700 bg-slate-900/50 mb-6">
                <CardContent className="p-6">
                  <h3 className="text-white font-bold mb-4">Fibonacci Geri Çekilme Seviyeleri</h3>
                  <div className="space-y-2">
                    {fibLevels.map((fib, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 rounded bg-slate-800/50">
                        <span className={`font-mono font-bold ${fib.color}`}>{fib.level}</span>
                        <span className="text-slate-400 text-sm">{fib.desc}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Section 2 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <BarChart3 className="h-4 w-4 text-blue-400" />
                </div>
                Fibonacci Nasıl Çizilir?
              </h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
                    <TrendingUp className="h-4 w-4 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Yükseliş Trendinde</h3>
                    <p className="text-slate-400 text-sm">
                      <strong>Dip noktadan zirveye</strong> doğru çizin. Fiyat geri çekildiğinde 
                      Fibonacci seviyeleri potansiyel destek bölgelerini gösterir.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center shrink-0">
                    <TrendingDown className="h-4 w-4 text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Düşüş Trendinde</h3>
                    <p className="text-slate-400 text-sm">
                      <strong>Zirveden dip noktaya</strong> doğru çizin. Fiyat yükseldiğinde 
                      Fibonacci seviyeleri potansiyel direnç bölgelerini gösterir.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Inline Signal Box */}
            <section className="mb-12">
              <Card className="border-brand-green/30 bg-brand-green/5">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Zap className="h-6 w-6 text-brand-green" />
                    <span className="font-bold text-white">AI Sinyal Örneği</span>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-bold">SOL-USDT</span>
                      <Badge className="bg-green-500/20 text-green-400">LONG</Badge>
                    </div>
                    <p className="text-slate-400 text-sm mb-2">
                      Fiyat %61.8 Fibonacci desteğinde + RSI oversold + Bullish engulfing
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Activity className="h-3 w-3" />
                      <span>Altın oran desteği tespit edildi</span>
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm mb-4">
                    Borsamex, Fibonacci seviyelerini otomatik hesaplar ve diğer göstergelerle birlikte değerlendirir.
                  </p>
                  <Link to="/signals">
                    <Button variant="outline" className="border-brand-green text-brand-green hover:bg-brand-green/10 w-full">
                      Tüm Sinyalleri İncele <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </section>

            {/* Section 3 - Golden Ratio */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-brand-green/20 flex items-center justify-center">
                  <Target className="h-4 w-4 text-brand-green" />
                </div>
                %61.8 Altın Oran Neden Önemli?
              </h2>
              
              <p className="text-slate-300 mb-4">
                %61.8 seviyesi <strong className="text-brand-green">"altın oran"</strong> olarak bilinir ve 
                Fibonacci dizisinde ardışık sayıların birbirine bölümünden elde edilir (örn: 89/144 ≈ 0.618).
              </p>
              
              <p className="text-slate-300 mb-6">
                Bu oran doğada (deniz kabukları, çiçekler, galaksiler), sanatta (Mona Lisa, Parthenon) ve 
                finansal piyasalarda sıkça görülür. Trading'de en güçlü geri çekilme desteği/direnci olarak kabul edilir.
              </p>
              
              <Card className="border-brand-green/30 bg-brand-green/5">
                <CardContent className="p-4">
                  <p className="text-slate-300 text-sm">
                    <strong className="text-brand-green">Pro Tip:</strong> %38.2-%61.8 arasındaki bölge 
                    "optimal geri çekilme bölgesi" olarak kabul edilir. Bu bölgede RSI veya 
                    <Link to="/rehber/macd-nedir" className="text-brand-green hover:underline mx-1">MACD</Link> 
                    konfirmasyonu arayın.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Section 4 - AI Trading */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Brain className="h-4 w-4 text-purple-400" />
                </div>
                AI Trading'de Fibonacci
              </h2>
              
              <p className="text-slate-300 mb-4">
                Borsamex'in Sniper Engine'i Fibonacci seviyelerini şu şekilde kullanır:
              </p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span><strong className="text-white">Otomatik swing tespiti:</strong> Önemli zirve ve dipler otomatik belirlenir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span><strong className="text-white">Çoklu zaman dilimi:</strong> Farklı periyotlardaki Fibonacci seviyeleri karşılaştırılır</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span><strong className="text-white">Confluence bölgeleri:</strong> Fibonacci + <Link to="/rehber/destek-direnc-nedir" className="text-brand-green hover:underline">destek/direnç</Link> çakışımları öncelikli</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span><strong className="text-white">Extension hedefleri:</strong> %127.2 ve %161.8 kar alma seviyeleri olarak değerlendirilir</span>
                </li>
              </ul>
              
              <Card className="border-amber-500/30 bg-amber-950/20">
                <CardContent className="p-4">
                  <p className="text-amber-200 text-sm flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                    Fibonacci seviyeleri kesin değil, olasılık bölgeleridir. Her zaman diğer göstergelerle birlikte değerlendirin.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* FAQ */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6">Sık Sorulan Sorular</h2>
              <div className="space-y-4">
                {faqData.map((faq, idx) => (
                  <Card key={idx} className="border-slate-700/50 bg-slate-900/30">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-white mb-2 flex items-start gap-2">
                        <span className="text-brand-green">?</span>
                        {faq.question}
                      </h3>
                      <p className="text-slate-400 text-sm pl-5">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            {/* Related Articles */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6">İlgili Makaleler</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/rehber/destek-direnc-nedir">
                  <Card className="border-slate-700/50 bg-slate-900/30 hover:border-brand-green/50 transition-colors">
                    <CardContent className="p-4">
                      <h3 className="text-white font-bold mb-1">Destek ve Direnç Nedir?</h3>
                      <p className="text-slate-400 text-sm">Fibonacci ile birlikte kullanılan kritik seviyeler</p>
                    </CardContent>
                  </Card>
                </Link>
                <Link to="/rehber/rsi-nedir">
                  <Card className="border-slate-700/50 bg-slate-900/30 hover:border-brand-green/50 transition-colors">
                    <CardContent className="p-4">
                      <h3 className="text-white font-bold mb-1">RSI Nedir?</h3>
                      <p className="text-slate-400 text-sm">Fibonacci seviyelerinde konfirmasyon için</p>
                    </CardContent>
                  </Card>
                </Link>
                <Link to="/rehber/macd-nedir">
                  <Card className="border-slate-700/50 bg-slate-900/30 hover:border-brand-green/50 transition-colors">
                    <CardContent className="p-4">
                      <h3 className="text-white font-bold mb-1">MACD Nedir?</h3>
                      <p className="text-slate-400 text-sm">Momentum ve trend analizi</p>
                    </CardContent>
                  </Card>
                </Link>
                <Link to="/rehber/tp-ve-stop-loss-nedir">
                  <Card className="border-slate-700/50 bg-slate-900/30 hover:border-brand-green/50 transition-colors">
                    <CardContent className="p-4">
                      <h3 className="text-white font-bold mb-1">TP ve Stop Loss Nedir?</h3>
                      <p className="text-slate-400 text-sm">Fibonacci hedeflerinde kar alma</p>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </section>

            <ShareBox 
              title="Fibonacci Nedir? Kripto Teknik Analiz Rehberi"
              url="/rehber/fibonacci-nedir"
              hashtags="#kripto #fibonacci #teknikanaliz #trading"
            />

          </motion.div>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-8 border-t border-slate-800">
            <Link to="/rehber/macd-nedir" className="flex items-center gap-2 text-slate-400 hover:text-white">
              <ArrowLeft className="h-4 w-4" />
              Önceki: MACD Nedir?
            </Link>
            <Link to="/rehber/destek-direnc-nedir" className="flex items-center gap-2 text-slate-400 hover:text-white">
              Sonraki: Destek ve Direnç
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-amber-900/20 to-brand-green/10 rounded-2xl border border-slate-700/50 text-center">
            <h3 className="text-xl font-bold text-white mb-3">
              Fibonacci + 200 Gösterge Birlikte Analiz
            </h3>
            <p className="text-slate-400 mb-6">
              Borsamex'in AI sistemi Fibonacci seviyelerini otomatik hesaplar ve diğer göstergelerle birlikte değerlendirir.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8">
                Ücretsiz Dene <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>

        </div>
      </article>
    </div>
  );
};

export default FibonacciNedirPage;
